import React from 'react';
import { cn } from '../../utils';

export type AdminTabVariant = 'segmented' | 'underline' | 'pills';

export interface AdminTabItem<T extends string = string> {
  id: T;
  label: string;
  /** Lucide icon or component that accepts `className` */
  icon?: React.ComponentType<{ className?: string }>;
}

export interface AdminTabsProps<T extends string = string> {
  tabs: AdminTabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  variant?: AdminTabVariant;
  /** Class for outer wrapper (segmented / pills) or underline bar */
  className?: string;
  /** Additional class for the nav section */
  navClassName?: string;
  'aria-label'?: string;
}

/**
 * Standardized tab navigation for admin: segmented (content groups), underline (browser-style), pills (status filters).
 */
function AdminTabs<T extends string>({
  tabs,
  value,
  onChange,
  variant = 'segmented',
  className,
  navClassName,
  'aria-label': ariaLabel = 'Tabs',
}: AdminTabsProps<T>) {
  if (variant === 'segmented') {
    return (
      <div
        className={cn(
          'inline-flex rounded-xl border border-border/60 bg-muted/30 p-1',
          className,
        )}
      >
        <nav className={cn('flex flex-wrap gap-0.5', navClassName)} aria-label={ariaLabel}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = value === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onChange(tab.id)}
                className={cn(
                  'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
              >
                {Icon != null && <Icon className={cn('h-4 w-4', isActive && 'text-primary')} />}
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  if (variant === 'underline') {
    return (
      <div className={cn('border-b border-border', className)}>
        <nav className={cn('flex flex-wrap gap-1', navClassName)} aria-label={ariaLabel}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = value === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onChange(tab.id)}
                className={cn(
                  'flex items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition-colors sm:px-4',
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground',
                )}
              >
                {Icon != null && <Icon className="h-4 w-4 shrink-0" />}
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  /* pills */
  return (
    <nav className={cn('flex flex-wrap gap-2', className, navClassName)} aria-label={ariaLabel}>
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = value === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={cn(
              'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
            )}
          >
            {Icon != null && <Icon className="h-4 w-4 shrink-0" />}
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}

export default AdminTabs;
